/**
 * MCP Resource Registry
 * Manages CRM resources that can be read by MCP clients
 */

import { MCPResource, MCPResourceContent, MCPExecutionContext } from '../types';

interface ResourceProvider {
  resource: MCPResource;
  provider: (uri: string, context: MCPExecutionContext) => Promise<MCPResourceContent>;
  registeredAt: Date;
}

export class MCPResourceRegistry {
  private resources: Map<string, ResourceProvider> = new Map();
  private initialized: boolean = false;

  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    console.log('📚 Initializing MCP Resource Registry...');
    
    // Register default resources
    this.registerDefaultResources();
    
    this.initialized = true;
    console.log(`✅ Resource Registry initialized with ${this.resources.size} resources`);
  }

  /**
   * Register default CRM resources
   */
  private registerDefaultResources(): void {
    // Leads resource
    this.registerResource(
      {
        uri: 'aideline://leads',
        name: 'Leads Database',
        description: 'Access to all leads in the CRM',
        mimeType: 'application/json'
      },
      async (uri, context) => {
        // This will be implemented with actual lead fetching
        return {
          uri,
          mimeType: 'application/json',
          text: JSON.stringify({ leads: [] }, null, 2)
        };
      }
    );

    // Opportunities resource
    this.registerResource(
      {
        uri: 'aideline://opportunities',
        name: 'Opportunities Database',
        description: 'Access to all opportunities in the CRM',
        mimeType: 'application/json'
      },
      async (uri, context) => {
        return {
          uri,
          mimeType: 'application/json',
          text: JSON.stringify({ opportunities: [] }, null, 2)
        };
      }
    );

    // Tasks resource
    this.registerResource(
      {
        uri: 'aideline://tasks',
        name: 'Tasks Database',
        description: 'Access to all tasks in the CRM',
        mimeType: 'application/json'
      },
      async (uri, context) => {
        return {
          uri,
          mimeType: 'application/json',
          text: JSON.stringify({ tasks: [] }, null, 2)
        };
      }
    );

    // Companies resource
    this.registerResource(
      {
        uri: 'aideline://companies',
        name: 'Companies Database',
        description: 'Access to all companies in the CRM',
        mimeType: 'application/json'
      },
      async (uri, context) => {
        return {
          uri,
          mimeType: 'application/json',
          text: JSON.stringify({ companies: [] }, null, 2)
        };
      }
    );

    // Org info resource
    this.registerResource(
      {
        uri: 'aideline://org/info',
        name: 'Organization Information',
        description: 'Information about the current organization',
        mimeType: 'application/json'
      },
      async (uri, context) => {
        return {
          uri,
          mimeType: 'application/json',
          text: JSON.stringify({
            orgId: context.orgId,
            orgName: context.orgConfig.orgName,
            features: context.orgConfig.features
          }, null, 2)
        };
      }
    );
  }

  /**
   * Register a resource
   */
  registerResource(
    resource: MCPResource,
    provider: (uri: string, context: MCPExecutionContext) => Promise<MCPResourceContent>
  ): void {
    if (this.resources.has(resource.uri)) {
      console.warn(`⚠️  Resource ${resource.uri} is already registered, overwriting...`);
    }

    this.resources.set(resource.uri, {
      resource,
      provider,
      registeredAt: new Date()
    });

    console.log(`✅ Registered resource: ${resource.uri}`);
  }

  /**
   * List all resources (filtered by org permissions and paginated)
   */
  async listResources(context: MCPExecutionContext, cursor?: string, limit?: number): Promise<{ resources: MCPResource[], nextCursor?: string }> {
    const allowedResources = context.orgConfig.mcp.allowedResources;
    const allResources = Array.from(this.resources.values());

    // Filter by allowed resources
    let filteredResources = allResources;
    if (allowedResources.length > 0 && !allowedResources.includes('*')) {
      filteredResources = allResources.filter(rp => {
        return allowedResources.some(pattern => {
          if (pattern.endsWith('*')) {
            return rp.resource.uri.startsWith(pattern.slice(0, -1));
          }
          return rp.resource.uri === pattern;
        });
      });
    }

    // Pagination
    const pageSize = limit || filteredResources.length;
    const startIndex = cursor ? parseInt(cursor, 10) : 0;
    const paginatedResources = filteredResources.slice(startIndex, startIndex + pageSize);
    
    const nextCursor = (startIndex + pageSize < filteredResources.length) 
      ? (startIndex + pageSize).toString() 
      : undefined;

    return {
      resources: paginatedResources.map(rp => rp.resource),
      nextCursor
    };
  }

  /**
   * Read a resource
   */
  async readResource(
    uri: string,
    context: MCPExecutionContext
  ): Promise<MCPResourceContent> {
    const provider = this.resources.get(uri);
    
    if (!provider) {
      throw new Error(`Resource not found: ${uri}`);
    }

    // Check if resource is allowed for this org
    const allowedResources = context.orgConfig.mcp.allowedResources;
    if (
      allowedResources.length > 0 && 
      !allowedResources.includes('*') && 
      !allowedResources.some(pattern => {
        if (pattern.endsWith('*')) {
          return uri.startsWith(pattern.slice(0, -1));
        }
        return uri === pattern;
      })
    ) {
      throw new Error(`Resource ${uri} is not allowed for this organization`);
    }

    // Fetch the resource
    const startTime = Date.now();
    try {
      const content = await provider.provider(uri, context);
      const duration = Date.now() - startTime;
      
      console.log(`✅ Resource read: ${uri} in ${duration}ms`);
      
      return content;
    } catch (error: any) {
      const duration = Date.now() - startTime;
      console.error(`❌ Resource read failed: ${uri} after ${duration}ms`, error);
      throw error;
    }
  }

  /**
   * Get resource count
   */
  getResourceCount(): number {
    return this.resources.size;
  }

  /**
   * Get all resource URIs
   */
  getResourceUris(): string[] {
    return Array.from(this.resources.keys());
  }

  /**
   * Check if resource exists
   */
  hasResource(uri: string): boolean {
    return this.resources.has(uri);
  }

  /**
   * Unregister a resource
   */
  unregisterResource(uri: string): boolean {
    return this.resources.delete(uri);
  }

  /**
   * Clear all resources
   */
  clear(): void {
    this.resources.clear();
  }
}

